/**
 * Chat message notification tone.
 *
 * Rings a short two-note chime when an employee (client) sends a chat message
 * so the agent notices it even while working elsewhere. Tone synthesis lives
 * in audioTone (shared with the DTMF keypad tones).
 *
 * Both chat subscriptions (useChatConnectSubscriptions for all active chats
 * and useChatConnectTranscript for the viewed chat) receive the same Connect
 * onMessage event, so the util de-duplicates: the same message key is skipped
 * if it already rang within the last few seconds.
 */

import type { CallMessage } from '@/types/call';
import { getAudioContext, playToneBurst } from '@/utils/audioTone';

/** Chime notes: first note then a slightly higher, longer second note. */
const CHIME_NOTES = [
  { frequency: 880, startOffsetSeconds: 0, durationSeconds: 0.12 },
  { frequency: 1320, startOffsetSeconds: 0.12, durationSeconds: 0.18 },
];

const TONE_GAIN = 0.1;
const DEDUPE_WINDOW_MS = 2000;

let lastPlayedKey: string | null = null;
let lastPlayedAt = 0;

function messageKey(message: CallMessage): string {
  const text = (message.message ?? '').trim().replace(/\s+/g, ' ');
  return message.messageId ?? `${text}|${message.timestamp ?? ''}`;
}

/**
 * Plays the notification chime for a client chat message.
 * Silent for agent/bot/summary messages and for a message that just rang.
 */
export function playChatMessageTone(message: CallMessage): void {
  if (message.talker !== 'client') return;

  const key = messageKey(message);
  const now = Date.now();
  if (key === lastPlayedKey && now - lastPlayedAt < DEDUPE_WINDOW_MS) return;
  lastPlayedKey = key;
  lastPlayedAt = now;

  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state === 'closed') return;
    const startAt = ctx.currentTime;
    for (const note of CHIME_NOTES) {
      playToneBurst(ctx, {
        frequency: note.frequency,
        startTime: startAt + note.startOffsetSeconds,
        durationSeconds: note.durationSeconds,
        gain: TONE_GAIN,
      });
    }
  } catch {
    // Audio unavailable (headless browser, autoplay policy) — message still shows.
  }
}
